import { useCallback, useEffect, useState } from "react";
import { Gamepad2, LockKeyhole, MessageSquareText, Network, RefreshCw, ShieldCheck, UsersRound } from "lucide-react";
import { ModuleGuide, useModuleCopy } from "./ModuleGuide";

import {
  acquireLease,
  addStudioComment,
  appendStudioCrdtUpdate,
  createStudioQuest,
  ingestStudioNarrativeEvent,
  loadCollaboration,
  loadWorldGame,
  type CollaborationSnapshot,
  type StudioAuth,
  type WorldGameSnapshot,
} from "../api/client";

type Props = { apiOrigin: string; projectId: string; unitId: string; unitTitle: string; headCommitId: string; auth: StudioAuth };

export function CollaborationWorldGamePanel({ apiOrigin, projectId, unitId, unitTitle, headCommitId, auth }: Props) {
  const [collaboration, setCollaboration] = useState<CollaborationSnapshot | null>(null);
  const [worldGame, setWorldGame] = useState<WorldGameSnapshot | null>(null);
  const [leaseToken, setLeaseToken] = useState("");
  const copy = useModuleCopy();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const refresh = useCallback(async () => {
    const [nextCollaboration, nextWorldGame] = await Promise.all([
      loadCollaboration({ apiOrigin, projectId, auth }), loadWorldGame({ apiOrigin, projectId, auth }),
    ]);
    setCollaboration(nextCollaboration); setWorldGame(nextWorldGame);
  }, [apiOrigin, auth, projectId]);
  useEffect(() => { void refresh().catch((cause: unknown) => setError(cause instanceof Error ? cause.message : "协作状态加载失败。")); }, [refresh]);

  async function act(key: string, operation: () => Promise<string>) {
    setBusy(key); setError(""); setNotice("");
    try { setNotice(await operation()); await refresh(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "操作失败。"); }
    finally { setBusy(null); }
  }

  return (
    <main className="production-panel" aria-label="协作与 World Game 工作区">
      <header className="production-header">
        <div><span className="eyebrow">COLLABORATION STATE ≠ CANONICAL LEDGER</span><h1>协作与 World Game · {unitTitle}</h1><p>单一活跃作者、评论、建议与轻量 CRDT 不直接改变 Canon；Narrative Event 只解锁叙事对象。</p></div>
        <button type="button" disabled={busy !== null} onClick={() => { void act("refresh", async () => { await refresh(); return "协作与叙事桥状态已刷新。"; }); }}><RefreshCw />刷新</button>
      </header>
      {busy ? <p role="status">{copy.working}</p> : null}
      {notice ? <p className="production-notice" role="status"><ShieldCheck />{notice}</p> : null}
      {error ? <p className="studio-inline-error" role="alert">{error}</p> : null}
      <ModuleGuide module="collaboration" />
      <section className="production-grid">
        <article className="production-card">
          <div className="context-card-title"><UsersRound /><div><h2>Collaboration MVP</h2><p>single active author · comments · suggestions · version · branch</p></div></div>
          <form className="module-comment" onSubmit={(event) => { event.preventDefault(); if (!comment.trim() || busy) return; void act("comment", async () => { await addStudioComment({ apiOrigin, projectId, unitId, body: comment.trim(), auth }); setComment(""); return copy.done; }); }}>
            <label>{copy.comment}<textarea required maxLength={8000} disabled={busy !== null} value={comment} onChange={(event) => setComment(event.target.value)} /></label>
            <button type="submit" disabled={!comment.trim() || busy !== null}><MessageSquareText />{copy.send}</button>
          </form>
          <div className="module-comments">{collaboration?.comments.filter((item) => item.unit_id === unitId).map((item) => <p key={String(item.id)}>{String(item.body ?? "")}</p>)}</div>
          <details className="module-advanced"><summary>{copy.advanced}</summary>
          <div className="production-actions">
            <button type="button" disabled={busy !== null} onClick={() => { void act("lease", async () => {
              const lease = await acquireLease({ apiOrigin, projectId, unitId, auth }); setLeaseToken(lease.leaseToken);
              return `已获得 120 秒活跃作者租约，截止 ${new Date(lease.expiresAt).toLocaleTimeString()}。`;
            }); }}><LockKeyhole />获取编辑租约</button>
            <button type="button" disabled={!leaseToken || busy !== null} onClick={() => { void act("crdt", async () => {
              const update = await appendStudioCrdtUpdate({ apiOrigin, projectId, unitId, headCommitId, leaseToken, auth });
              return update.canonicalMutation ? "异常：CRDT 改变了 Canon。" : "CRDT presence checkpoint 已保存；Canon HEAD 未改变。";
            }); }}><Network />写入 CRDT checkpoint</button>
          </div>
          </details>
          <div className="production-node-list">
            <div><span>active authors</span><strong>{collaboration?.activeAuthors.length ?? 0}</strong><em className="fresh">bounded lease</em></div>
            <div><span>comments</span><strong>{collaboration?.comments.length ?? 0}</strong><em className="fresh">branch scoped</em></div>
            <div><span>suggestions</span><strong>{collaboration?.suggestions.length ?? 0}</strong><em className="fresh">PendingChange</em></div>
            <div><span>crdt updates</span><strong>{collaboration?.crdt.updates.length ?? 0}</strong><em className="fresh">non-canonical</em></div>
          </div>
          <p className="empty-world">复杂实时 Yjs / Automerge + Durable Objects 延后；当前使用有界 operation log，并保持 Canonical Ledger 为唯一 Story truth。</p>
        </article>

        <article className="production-card">
          <div className="context-card-title"><Gamepad2 /><div><h2>Narrative Event Bridge</h2><p>Quest / NPC Knowledge / Dialogue Node</p></div></div>
          <details className="module-advanced"><summary>{copy.advanced}</summary>
          <div className="production-actions">
            <button type="button" disabled={busy !== null} onClick={() => { void act("quest", async () => {
              await createStudioQuest({ apiOrigin, projectId, title: "作者审核任务", eventType: "player_helped_npc_a", auth });
              return "示例 Quest 已登记，等待 player_helped_npc_a 叙事事件解锁。";
            }); }}>创建示例 Quest</button>
            <button type="button" disabled={busy !== null} onClick={() => { void act("event", async () => {
              const event = await ingestStudioNarrativeEvent({ apiOrigin, projectId, eventType: "player_helped_npc_a", auth });
              return `叙事事件已处理：${event.unlocks.length} 个解锁；Game state mutated = ${String(event.gameStateMutated)}。`;
            }); }}>模拟 Narrative Event</button>
          </div>
          <div className="production-node-list">
            <div><span>NPCs</span><strong>{worldGame?.npcs.length ?? 0}</strong><em className="fresh">Story-owned</em></div>
            <div><span>knowledge</span><strong>{worldGame?.knowledge.length ?? 0}</strong><em className="fresh">Canon-linked</em></div>
            <div><span>quests</span><strong>{worldGame?.quests.length ?? 0}</strong><em className="fresh">definitions</em></div>
            <div><span>dialogue nodes</span><strong>{worldGame?.dialogueNodes.length ?? 0}</strong><em className="fresh">branch conditions</em></div>
            <div><span>narrative events</span><strong>{worldGame?.events.length ?? 0}</strong><em className="fresh">idempotent</em></div>
            <div><span>unlocks</span><strong>{worldGame?.unlocks.length ?? 0}</strong><em className="fresh">quest/dialogue only</em></div>
          </div>
          <p className="empty-world">拒绝存储 Player position、HP、inventory、combat、currency、runtime economy/weather 与 player session；Game tick-level state 始终由 Game Runtime 持有。</p>
          </details>
        </article>
      </section>
    </main>
  );
}
